import { useState, useEffect, useRef } from "react";
import { Search, MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { popularAreas } from "@/data/nigeriaStates";
import { countries, getCountryByStateId, DEFAULT_COUNTRY_ID } from "@/data/countries";
import { categories } from "@/data/categories";
import { cn } from "@/lib/utils";

interface SearchAutocompleteProps {
  onSearch: (searchTerm: string, location: string) => void;
  initialSearchTerm?: string;
  initialLocation?: string;
}

interface Suggestion {
  type: "category" | "subcategory" | "popular" | "area";
  label: string;
  icon: string;
  parentCategory?: string;
  stateLabel?: string;
}

// Generate all searchable suggestions from categories
const generateSuggestions = (): Suggestion[] => {
  const suggestions: Suggestion[] = [];
  
  // Add popular searches
  const popularSearches = ["Plumber", "Electrician", "Mechanic", "Cleaner", "Photographer", "Hair Stylist", "AC Repair", "Carpenter"];
  popularSearches.forEach(term => {
    suggestions.push({ type: "popular", label: term, icon: "🔥" });
  });
  
  // Add categories
  categories.forEach(category => {
    suggestions.push({
      type: "category",
      label: category.name,
      icon: category.icon,
    });
    
    // Add subcategories
    category.subCategories.forEach(sub => {
      suggestions.push({
        type: "subcategory",
        label: sub.name,
        icon: sub.icon,
        parentCategory: category.name,
      });
    });
  });

  // Add popular areas
  popularAreas.forEach(area => {
    suggestions.push({
      type: "area",
      label: area.name,
      icon: "📍",
      stateLabel: area.stateLabel,
    });
  });
  
  return suggestions;
};

const allSuggestions = generateSuggestions();

export function SearchAutocomplete({ 
  onSearch, 
  initialSearchTerm = "", 
  initialLocation = "" 
}: SearchAutocompleteProps) {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedLocation, setSelectedLocation] = useState(initialLocation);
  const [countryId, setCountryId] = useState(
    getCountryByStateId(initialLocation)?.id || DEFAULT_COUNTRY_ID,
  );
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState<Suggestion[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (searchTerm.length === 0) {
      // Show popular searches when empty
      setFilteredSuggestions(allSuggestions.filter(s => s.type === "popular").slice(0, 8));
    } else {
      // Filter suggestions based on input
      const filtered = allSuggestions.filter(s => 
        s.label.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 10);
      setFilteredSuggestions(filtered);
    }
    setHighlightedIndex(-1);
  }, [searchTerm]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    setShowSuggestions(false);
    onSearch(searchTerm, selectedLocation);
  };

  const handleSuggestionClick = (suggestion: Suggestion) => {
    setSearchTerm(suggestion.label);
    setShowSuggestions(false);
    onSearch(suggestion.label, selectedLocation);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showSuggestions) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case "Enter":
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < filteredSuggestions.length) {
          handleSuggestionClick(filteredSuggestions[highlightedIndex]);
        } else {
          handleSearch();
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
    }
  };

  const handleQuickSearch = (term: string) => {
    setSearchTerm(term);
    onSearch(term, selectedLocation);
  };

  return (
    <div ref={containerRef} className="rounded-2xl bg-card p-4 shadow-soft md:p-6">
      <form onSubmit={handleSearch} className="flex flex-col gap-4 md:flex-row">
        {/* Service Search with Autocomplete */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            type="text"
            placeholder="What service do you need?"
            className="h-12 pl-10 text-base"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            onKeyDown={handleKeyDown}
            autoComplete="off"
          />
          
          {/* Suggestions Dropdown */}
          {showSuggestions && filteredSuggestions.length > 0 && (
            <div className="absolute left-0 right-0 top-full z-50 mt-1 rounded-lg border bg-popover shadow-lg">
              <ScrollArea className="max-h-[280px]">
                <div className="p-2">
                  {searchTerm.length === 0 && (
                    <div className="mb-2 px-2 text-xs font-medium text-muted-foreground">
                      Popular Searches
                    </div>
                  )}
                  {filteredSuggestions.map((suggestion, index) => (
                    <button
                      key={`${suggestion.type}-${suggestion.label}`}
                      type="button"
                      className={cn(
                        "flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors",
                        highlightedIndex === index
                          ? "bg-accent text-accent-foreground"
                          : "hover:bg-muted"
                      )}
                      onClick={() => handleSuggestionClick(suggestion)}
                      onMouseEnter={() => setHighlightedIndex(index)}
                    >
                      <span className="text-lg">{suggestion.icon}</span>
                      <div className="flex-1">
                        <div className="font-medium">{suggestion.label}</div>
                        {suggestion.parentCategory && (
                          <div className="text-xs text-muted-foreground">
                            in {suggestion.parentCategory}
                          </div>
                        )}
                        {suggestion.stateLabel && (
                          <div className="text-xs text-muted-foreground">
                            {suggestion.stateLabel}
                          </div>
                        )}
                      </div>
                      {suggestion.type === "popular" && (
                        <span className="text-xs text-muted-foreground">Popular</span>
                      )}
                      {suggestion.type === "area" && (
                        <span className="text-xs text-primary">Area</span>
                      )}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}
        </div>

        {/* Country Select */}
        <div className="relative md:w-44">
          <Select
            value={countryId}
            onValueChange={(v) => {
              setCountryId(v);
              setSelectedLocation("");
            }}
          >
            <SelectTrigger className="h-12" aria-label="Country">
              <SelectValue placeholder="Country" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {countries.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.flag} {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Location Select */}
        <div className="relative md:w-56">
          <MapPin className="absolute left-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Select value={selectedLocation || "all"} onValueChange={(val) => setSelectedLocation(val === "all" ? "" : val)}>
            <SelectTrigger className="h-12 pl-10" aria-label="Location">
              <SelectValue placeholder="Select Location" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All Locations</SelectItem>
              {(countries.find((c) => c.id === countryId)?.states ?? []).map((state) => (
                <SelectItem key={state.id} value={state.id}>
                  {state.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Button */}
        <Button type="submit" size="lg" className="h-12 px-8 text-base font-semibold">
          Search
        </Button>
      </form>

      {/* Popular Searches */}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-muted-foreground">Popular:</span>
        {["Plumber", "Electrician", "Mechanic", "Cleaner", "Photographer"].map(
          (term) => (
            <button
              key={term}
              type="button"
              onClick={() => handleQuickSearch(term)}
              className="rounded-full bg-muted px-3 py-1 text-muted-foreground transition-colors hover:bg-primary hover:text-primary-foreground"
            >
              {term}
            </button>
          )
        )}
      </div>
    </div>
  );
}
